import { Module, Mutation, VuexModule, Action } from "vuex-module-decorators";
import { formatProductData, joinChar } from "@/utils/mall/mall";
import { RootState } from "@/store";
import { queryRequest } from "@/utils/request";
import { queryCouponList } from "@/graphql/coupon.gql";
import { wtCalculateCheckOut } from "@/graphql/cart.gql";
import { createOrderWt } from "@/graphql/order.gql";
import { GTGroupFixedOrgParam, GTGroupFixedParam } from "@/enums/app";
import { DateClass, DateFormatClass } from "@/utils/common/date";

const pickCoupons = (coupons = []) => {
  return coupons.map(
    ({ activityCode, activityName, couponCode, couponType }) => ({
      activityCode,
      activityName,
      couponCode,
      couponType
    })
  );
};

export interface MallOrderConfirmState {
  selectExpress: number | undefined | null;
  couponData: any[];
  selectedCoupons: any[];
  orderCode: string;
  settleData: any;
  canSubmit: boolean;
}

@Module({ name: "MallOrderConfirmModule", namespaced: true })
export default class MallOrderConfirmModule
  extends VuexModule<MallOrderConfirmState, RootState>
  implements MallOrderConfirmState {
  // 结算是否选择快递
  selectExpress = null;
  // 可用的优惠券列表
  couponData = [];
  // 选中的优惠券数组
  selectedCoupons = [];
  // 下单成功的订单号
  orderCode = "";
  // 结算数据，优惠券计算后的
  settleData = {};
  // 按钮控制
  canSubmit = false;

  @Mutation
  saveCanSubmitMutation(data = false) {
    this.canSubmit = data;
  }

  @Mutation
  saveSelectExpressMutation(data: number | null | undefined) {
    this.selectExpress = data;
  }
  @Mutation
  saveCouponDataMutation(data: any[]) {
    this.couponData = data ?? [];
  }

  @Mutation
  saveSelectedCouponMutation(data: any[] | null | undefined) {
    this.selectedCoupons = data ?? [];
  }

  @Mutation
  saveOrderCodeMutation(data = "") {
    this.orderCode = data ?? "";
  }
  @Mutation
  saveSettleDataMutation(data = {}) {
    this.settleData = data ?? {};
  }

  // 下单成功，清空数据
  @Mutation
  clearOrderConfirmMutation() {
    this.selectExpress = null;
    this.couponData = [];
    this.selectedCoupons = [];
    this.settleData = {};
  }

  // 查询优惠券，并且结算，获取价格
  @Action
  async queryAndSettleCouponAction(params: any = {}) {
    const {
      state: { selectExpress }
    } = this.context;
    const {
      products = [],
      couponAcquireScene = "2", // 使用选择的优惠券
      coupons: tempCoupons = [] // 临时选中的优惠券
    } = params;
    const data = await queryRequest({
      query: wtCalculateCheckOut,
      variables: {
        input: {
          // ...GTGroupFixedParam,
          cartType: selectExpress == 2 ? "8" : "7",
          couponAcquireScene,
          storeInputs: [
            {
              couponInputList: pickCoupons(tempCoupons),
              productInputList: products.map(
                ({ count, productCode, skuCode }) => ({
                  count,
                  skuCode,
                  productCode,
                  selectFlag: "01",
                  isExpress: selectExpress,
                  ...GTGroupFixedOrgParam
                })
              )
            }
          ]
        }
      }
    });
    // 优惠券处理数据
    this.context.commit(
      "saveCouponDataMutation",
      data?.checkOutStoreList?.[0]?.couponList?.map(o => {
        o.isReward = !+o.isDisable;
        o.validEndTime = DateFormatClass.normal(
          DateClass.small(o.validEndTime)
        );
        o.validStartTime = DateFormatClass.normal(
          DateClass.small(o.validStartTime)
        );
        return o;
      })
    );

    // 结算数据处理
    const { commit } = this.context;
    const settleData = data?.checkOutStoreList[0] ?? {};
    settleData.checkOutProductList = settleData.checkOutProductList.map(item =>
      formatProductData(item, true)
    );
    commit("saveSettleDataMutation", settleData);
    commit("saveCanSubmitMutation", true);
  }

  // 优惠券结算接口，获取价格
  @Action
  async settleByCouponAction(data = []) {
    const {
      state: { selectedCoupons, selectExpress }
    } = this.context;
    const { checkOutStoreList = [] } = await queryRequest({
      query: wtCalculateCheckOut,
      variables: {
        input: {
          cartType: selectExpress == 2 ? "8" : "7",
          // ...GTGroupFixedParam,
          storeInputs: [
            {
              couponInputList: pickCoupons(selectedCoupons),
              productInputList: data.map(({ skuCode, _select, count }) => ({
                selectFlag: _select ? "01" : "00",
                skuCode,
                count,
                isExpress: selectExpress,
                ...GTGroupFixedOrgParam
              }))
            }
          ]
        }
      }
    });

    const { commit } = this.context;
    const settleData = checkOutStoreList[0] ?? {};
    settleData.checkOutProductList = settleData.checkOutProductList.map(item =>
      formatProductData(item, true)
    );
    commit("saveSettleDataMutation", settleData);
    commit("saveCanSubmitMutation", true);
  }

  // 查询优惠券列表
  @Action
  async queryCouponDataAction(params) {
    const {
      state: { selectExpress }
    } = this.context;
    const {
      products,
      couponAcquireScene = "2", // 使用选择的优惠券
      coupons: tempCoupons = [] //  临时选中的优惠券
    } = params;
    const data = await queryRequest({
      query: queryCouponList,
      variables: {
        input: {
          ...GTGroupFixedParam,
          couponAcquireScene,
          storeInputs: [
            {
              couponInputList: pickCoupons(tempCoupons),
              productInputList: products.map(
                ({ count, productCode, skuCode }) => ({
                  count,
                  skuCode,
                  productCode,
                  selectFlag: "01",
                  isExpress: selectExpress,
                  ...GTGroupFixedOrgParam
                })
              )
            }
          ]
        }
      }
    });
    this.context.commit(
      "saveCouponDataMutation",
      data?.checkOutStoreList?.[0]?.couponList?.map(o => {
        o.isReward = !+o.isDisable;
        o.validEndTime = DateFormatClass.normal(
          DateClass.small(o.validEndTime)
        );
        o.validStartTime = DateFormatClass.normal(
          DateClass.small(o.validStartTime)
        );
        return o;
      })
    );
  }

  // 下单、提交订单
  @Action
  async createOrderAction({
    data,
    storeInfo,
    settleData,
    paymentType,
    payType
  }) {
    const {
      state: { selectedCoupons, selectExpress }
    } = this.context;
    const { data: orderCode } = await queryRequest(
      {
        query: createOrderWt,
        variables: {
          request: {
            storeInputs: [
              {
                isExpress: selectExpress,
                orgCode: storeInfo.orgCode,
                storeCode: storeInfo.storeCode,
                couponInputList: pickCoupons(selectedCoupons),
                productInputList: data.map(
                  ({ skuCode, productCode, sellPrice, count }) => ({
                    isExpress: selectExpress,
                    count,
                    skuCode,
                    productCode,
                    // oldSellPrice: sellPrice,
                    ...GTGroupFixedOrgParam
                  })
                )
              }
            ],
            payType,
            payMentType:
              paymentType || (+settleData.amount === 0 ? "offline" : "online"), // 0元下单默认线下
            terminal: "51",
            couponAcquireScene: "2", // 计算优惠券
            // ...GTGroupFixedParam
            cartType: selectExpress == 2 ? "8" : "7"
          }
        }
      },
      { custom: true }
    );
    this.context.commit("saveOrderCodeMutation", orderCode);
  }
}
